class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.requestId = 0;
    this.resultPrefix = `search-${PredictiveSearch.instanceCount++}`;
    try {
      this.useStorefrontSearch = JSON.parse(document.getElementById('shopify-features')?.textContent || '{}').predictiveSearch === false;
    } catch (_) {
      this.useStorefrontSearch = false;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('input', () => {
      const selected = this.querySelector('[aria-selected="true"]');
      if (selected) selected.setAttribute('aria-selected', 'false');
      this.input.removeAttribute('aria-activedescendant');
      if (!this.getQuery().length) {
        ++this.requestId;
        this.abortController.abort();
        this.close(true);
      }
    });
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 300).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    this.searchTerm = this.getQuery();

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Escape':
        ++this.requestId;
        this.abortController.abort();
        this.close();
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll('li[role="option"], button[role="option"]')
    ).filter((element) => element.offsetParent !== null);
    if (!allVisibleElements.length) return;
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  async getSearchResults(searchTerm) {
    const queryKey = searchTerm.toLowerCase();
    const requestId = ++this.requestId;
    this.abortController.abort();
    this.abortController = new AbortController();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const fallbackUrl = new URL(this.input.form.action, window.location.origin);
    fallbackUrl.search = new URLSearchParams({
      q: searchTerm, type: 'product', 'options[prefix]': 'last',
      section_id: 'ls-search-suggestions'
    }).toString();
    const predictiveUrl = `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`;

    try {
      let response = await fetch(this.useStorefrontSearch ? fallbackUrl : predictiveUrl, {
        signal: this.abortController.signal
      });
      // Shopify's predictive API does not support Traditional Chinese buyer locales.
      if (!this.useStorefrontSearch && response.status === 417) {
        this.useStorefrontSearch = true;
        response = await fetch(fallbackUrl, { signal: this.abortController.signal });
      }
      if (!response.ok) throw new Error(`Search request failed: ${response.status}`);
      const text = await response.text();
      const sectionId = this.useStorefrontSearch ? 'ls-search-suggestions' : 'predictive-search';
      const section = new DOMParser().parseFromString(text, 'text/html').getElementById(`shopify-section-${sectionId}`);
      if (!section || !section.querySelector('[data-predictive-search-live-region-count-value]')) {
        throw new Error('Search response has no results section');
      }
      if (requestId !== this.requestId || this.getQuery() !== searchTerm) return;
      this.allPredictiveSearchInstances.forEach(instance => {
        instance.cachedResults[queryKey] = section.innerHTML;
      });
      this.renderSearchResults(section.innerHTML);
    } catch (error) {
      if (error.name === 'AbortError' || requestId !== this.requestId || this.getQuery() !== searchTerm) return;
      // Keep a usable full-search link if the network or suggestions endpoint fails.
      const link = document.createElement('a');
      const fullSearchUrl = new URL(this.input.form.action, window.location.origin);
      fullSearchUrl.search = new URLSearchParams({ q: searchTerm, type: 'product', 'options[prefix]': 'last' });
      link.href = fullSearchUrl;
      link.className = 'predictive-search__item';
      link.textContent = document.documentElement.lang.startsWith('zh')
        ? `查看「${searchTerm}」的搜尋結果 →` : `View results for “${searchTerm}” →`;
      this.predictiveSearchResults.replaceChildren(link);
      this.removeAttribute('loading');
      this.setLiveRegionText(link.textContent);
      if (this.contains(document.activeElement)) this.open();
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    // Desktop, mobile and search-page inputs need independent ARIA targets.
    const idMap = new Map();
    this.predictiveSearchResults.querySelectorAll('[id]').forEach(element => {
      const original = element.id;
      const id = `${this.resultPrefix}-${original}`;
      idMap.set(original, id);
      element.id = id;
    });
    this.predictiveSearchResults.querySelectorAll('[aria-labelledby]').forEach(element => {
      element.setAttribute('aria-labelledby', element.getAttribute('aria-labelledby').split(' ').map(id => idMap.get(id) || id).join(' '));
    });
    const listId = idMap.get('predictive-search-results-list');
    if (listId) {
      this.input.setAttribute('aria-controls', listId);
      this.input.setAttribute('aria-owns', listId);
    }
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    if (this.contains(document.activeElement)) this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.section-header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false
    this.predictiveSearchResults.removeAttribute('style');
  }
}

PredictiveSearch.instanceCount = 0;
customElements.define('predictive-search', PredictiveSearch);

